/**
 * This file is part of vue-boilerplate.
 * @link     : https://zhaiyiming.com/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 TINYMINS.
 */

import get from 'lodash/get';
import { UAParser } from 'ua-parser-js';
import { RouterMode } from 'vue-router';
import * as storage from './storage';

const ua = navigator.userAgent.toLowerCase();
const parser = new UAParser();
const browser = parser.getBrowser();

export const isInDevMode = (key = ''): boolean => {
  if (key) {
    return !!storage.getLocal(`dev-${key}`);
  }
  return process.env.NODE_ENV !== 'production';
};
export const setDevMode = (key: string, dev: boolean): void => (dev ? storage.setLocal(`dev-${key}`, dev) : storage.removeLocal(`dev-${key}`));

export const isRun = (): boolean => process.env.NODE_ACTION === 'run';
export const isDevhost = (): boolean => (/^dev\..*$/u).test(window.location.hostname);
export const isProdhost = (): boolean => (/^(?:haiman\.io|haimanchajian\.com)$/u).test(window.location.hostname);
export const isLocalhost = (): boolean => (/^(?:\d+.\d+.\d+.\d+|localhost)$/u).test(window.location.hostname);

export const isInSafari = (): boolean => browser.name === 'Safari' || browser.name === 'Mobile Safari';
export const isInEmbedded = (): boolean => (/(?:weibo|qq|micromessenger|hmapp\/)/u).test(ua);
export const isInHmApp = (): boolean => (/hmapp\//u).test(ua);
export const isInWechat = (): boolean => (/micromessenger/u).test(ua);
export const isInWechatMobile = (): boolean => isInWechat() && !(/(windows|mac)wechat/u).test(ua);
export const isInWechatDesktop = (): boolean => isInWechat() && (/windowswechat/u).test(ua);

export const isInWebAppiOS = (): boolean => !!get(window.navigator, 'standalone');
export const isInWebAppChrome = (): boolean => !!window.matchMedia('(display-mode: standalone)').matches;
export const isInMobileDevice = (): boolean => (/mobile/u).test(ua);
export const isIniOS = (): boolean => (/iphone/u).test(ua);
export const isInAppleWebkit = (): boolean => (/applewebkit\/([\d.]+)/u).test(ua);
export const getAppleWebkitVersion = (): string => ((/applewebkit\/([\d.]+)/iu).exec(ua) || [])[1] || '0';
export const isDarkEnabled = (): boolean => true;
export const isTalkEnabled = (): boolean => isInDevMode();

export type ColorTheme = 'user' | 'system' | 'auto';
/**
 * Get color scheme for the website.
 * If browser supports "prefers-color-scheme" it will respect the setting for light or dark mode
 * otherwise it will set a dark theme during night time
 * @param {ColorTheme[]} order 获取方式优先顺序
 * @returns {string} Theme name
 */
export const getColorTheme = (order: ColorTheme[] = ['user', 'system', 'auto']): string => {
  if (!isDarkEnabled()) {
    return 'light';
  }
  const colorTheme: Record<string, string> = {};
  const theme = storage.getLocal<string>('color-theme') || '';
  if (theme) {
    colorTheme.user = theme;
  }
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (isDarkMode) {
    colorTheme.system = 'dark';
  }
  const isLightMode = window.matchMedia('(prefers-color-scheme: light)').matches;
  if (isLightMode) {
    colorTheme.system = 'light';
  }
  const isNotSpecified = window.matchMedia('(prefers-color-scheme: no-preference)').matches;
  const hasNoSupport = !isDarkMode && !isLightMode && !isNotSpecified;
  if (isNotSpecified || hasNoSupport) {
    const now = new Date();
    const hour = now.getHours();
    colorTheme.auto = hour < 6 || hour >= 22 ? 'dark' : 'light';
  }
  return order.map(mode => colorTheme[mode]).filter(_ => _)[0] || 'light';
  // window.matchMedia('(prefers-color-scheme: dark)').addListener(e => e.matches && activateDarkMode())
  // window.matchMedia('(prefers-color-scheme: light)').addListener(e => e.matches && activateLightMode())
};

export const setColorTheme = (theme: string): void => {
  if (theme) {
    storage.setLocal('color-theme', theme);
  } else {
    storage.removeLocal('color-theme');
  }
};

export const getEyeProtectMode = (): string => (isDarkEnabled() ? '' : storage.getLocal<string>('eye_protect_mode') || '');
export const setEyeProtectMode = (mode: string): void => {
  if (mode) {
    storage.setLocal('eye_protect_mode', mode);
  } else {
    storage.removeLocal('eye_protect_mode');
  }
};

export const isInBrowser = (): boolean => typeof window !== 'undefined';
export const isSupportPushState = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }
  if (
    (ua.indexOf('android 2.') !== -1 || ua.indexOf('android 4.0') !== -1)
    && ua.indexOf('mobile safari') !== -1
    && ua.indexOf('chrome') === -1
    && ua.indexOf('windows phone') === -1
  ) {
    return false;
  }
  return window.history && 'pushState' in window.history;
};

export const getRouterMode = (): RouterMode => {
  if (process.env.ROUTER_MODE === 'hash' || process.env.ROUTER_MODE === 'history' || process.env.ROUTER_MODE === 'abstract') {
    return process.env.ROUTER_MODE;
  }
  // return isInWechatMobile() || !isSupportPushState() ? 'hash' : 'history';
  return isSupportPushState() ? 'history' : 'hash';
};

export const setProdDatabase = (prod): void => {
  storage.removeLocal('database');
  if (prod) storage.setLocal('database', 'prod');
};
export const isProdDatabase = (): boolean => isProdhost() || storage.getLocal('database') === 'prod';

export const isTimEnabled = (): boolean => false;
